import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UserCircle } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const GROUPS = [
  {
    title: "On the quote",
    rows: [
      [
        "show_prices",
        "Prices and totals",
        "Line prices, discounts, tax and the total",
      ],
      [
        "show_drawing",
        "Shop drawing",
        "The plan view with sizes, edges and cut-outs",
      ],
      ["show_layout", "Slab layout", "How their pieces are nested on the slab"],
      [
        "show_3d",
        "3D view",
        "Spin around the finished counters with the slab photo and edge profiles on",
      ],
      [
        "allow_accept",
        "Accept online",
        "Customer can tick, sign with their name and accept the quote",
      ],
    ],
  },
  {
    title: "On the project",
    rows: [
      [
        "show_job_progress",
        "Stage and progress",
        "Which stage the project has reached",
      ],
      [
        "show_job_dates",
        "Template, fab and install dates",
        "The booked dates and time windows",
      ],
      ["show_photos", "Project photos", "Photos your crew has added to the project"],
      ["show_documents", "Documents", "Files attached to the project"],
    ],
  },
  {
    title: "Invoices and payments",
    rows: [
      [
        "show_invoices",
        "Invoices and balance due",
        "Deposit and balance invoices with a printable copy",
      ],
      [
        "allow_online_payment",
        "Pay by card online",
        "Customer can pay the balance of an invoice with a card",
      ],
    ],
  },
  {
    title: "Talking to you",
    rows: [
      [
        "allow_notes",
        "Notes and replies",
        "Customer can send notes and read your replies",
      ],
    ],
  },
];

const PRICE_GROUPS = [
  "Contractor",
  "Designer",
  "Builder",
  "Commercial",
  "Regular",
];

const Row = ({ id, title, sub, value, onChange }) => (
  <div className="flex items-start justify-between gap-4 border-b border-zinc-800/70 py-3.5 last:border-0">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-zinc-100">{title}</div>
      <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>
    </div>
    <Switch
      data-testid={`portal-toggle-${id}`}
      checked={!!value}
      onCheckedChange={onChange}
    />
  </div>
);

const ColourAutoApplyCard = () => {
  const [on, setOn] = useState(null);
  useEffect(() => {
    api.get("/settings/colour-choice").then(({ data }) => setOn(!!data.auto_apply)).catch(() => setOn(false));
  }, []);
  const toggle = async (v) => {
    setOn(v);
    try {
      await api.put("/settings/colour-choice", { auto_apply: v });
      toast.success(v ? "Accepted colors will apply to the quote automatically" : "Staff will apply accepted colors by hand");
    } catch {
      setOn(!v);
      toast.error("Could not save");
    }
  };
  if (on === null) return null;
  return (
    <div className="mt-6 flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid="portal-colour-auto-apply">
      <div>
        <div className="text-sm font-semibold text-zinc-100">Auto-apply accepted colors</div>
        <div className="mt-0.5 text-xs text-zinc-500">
          When a customer accepts an alternate color in the portal, swap it onto the quote and reprice immediately — no staff click needed.
        </div>
      </div>
      <Switch data-testid="portal-colour-auto-apply-toggle" checked={on} onCheckedChange={toggle} />
    </div>
  );
};


export default function PortalSettings() {
  const nav = useNavigate();
  const [vis, setVis] = useState(null);
  const [busy, setBusy] = useState(false);
  const latest = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/portal");
    latest.current = data;
    setVis(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) =>
    setVis((prev) => {
      const next = { ...prev, [key]: value };
      latest.current = next;
      return next;
    });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/portal", latest.current);
      latest.current = data;
      setVis(data);
      toast.success("Customer portal settings saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save those settings",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Customer portal" />
      <main className="relative z-10 pb-36 pt-6">
        <button
          type="button"
          data-testid="portal-settings-back"
          onClick={() => nav("/settings")}
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
        >
          <ArrowLeft size={13} weight="bold" /> Settings
        </button>
        <div className="mt-3 flex items-center gap-3">
          <UserCircle size={22} weight="bold" className="text-indigo-300" />
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            What customers can see
          </h1>
        </div>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Turn parts of the customer portal on or off. This applies to every
          account that has a portal link.
        </p>

        {vis && (
          <div className="mt-6 space-y-4" data-testid="portal-settings">
            {GROUPS.map((g) => (
              <section
                key={g.title}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              >
                <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                  {g.title}
                </h2>
                <div className="mt-2">
                  {g.rows.map(([key, title, sub]) => (
                    <Row
                      key={key}
                      id={key}
                      title={title}
                      sub={sub}
                      value={vis[key]}
                      onChange={(v) => set(key, v)}
                    />
                  ))}
                </div>
              </section>
            ))}

            <section
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              data-testid="portal-inventory-pricing"
            >
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Inventory pricing
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                Show a price per sq ft on the slabs and remnants in the portal,
                and a price on each sink. Pick which account groups see it, and
                what each group pays off the list price. A price typed on an
                individual slab always wins.
              </p>
              <div className="mt-2 space-y-2">
                <Row
                  id="show_inventory_prices"
                  title="Show inventory prices"
                  sub="Master switch — off means no customer sees a slab/remnant price"
                  value={vis.show_inventory_prices}
                  onChange={(v) => set("show_inventory_prices", v)}
                />
                <Row
                  id="show_sink_prices"
                  title="Show sink prices"
                  sub="Separate switch just for sinks — slabs and remnants stay hidden"
                  value={vis.show_sink_prices}
                  onChange={(v) => set("show_sink_prices", v)}
                />
              </div>
              {vis.show_inventory_prices &&
                (vis.inventory_price_groups || []).length === 0 && (
                  <div
                    className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                    data-testid="portal-price-no-groups-warning"
                  >
                    Prices are switched on but no account group is set to “Sees
                    prices” below — so no customer will see a slab or remnant
                    price yet. Tick at least one group, or set an individual
                    account’s Price Mode to “Always on”.
                  </div>
                )}
              <div className="mt-4 space-y-2">
                {PRICE_GROUPS.map((g) => {
                  const on = (vis.inventory_price_groups || []).includes(g);
                  return (
                    <div
                      key={g}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
                    >
                      <button
                        type="button"
                        data-testid={`portal-price-group-${g}`}
                        onClick={() =>
                          set(
                            "inventory_price_groups",
                            on
                              ? (vis.inventory_price_groups || []).filter(
                                  (x) => x !== g,
                                )
                              : [...(vis.inventory_price_groups || []), g],
                          )
                        }
                        className={`h-9 rounded-lg px-4 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                          on
                            ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40"
                            : "border border-zinc-800 text-zinc-500 hover:text-zinc-200"
                        }`}
                      >
                        {on ? "Sees prices" : "Hidden"}
                      </button>
                      <span className="text-sm font-semibold text-zinc-200">
                        {g}
                      </span>
                      <label className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
                        Off list
                        <input
                          data-testid={`portal-price-discount-${g}`}
                          type="number"
                          step="0.5"
                          value={
                            (vis.inventory_group_discounts || {})[g] ?? 0
                          }
                          onChange={(e) =>
                            set("inventory_group_discounts", {
                              ...(vis.inventory_group_discounts || {}),
                              [g]: Number(e.target.value) || 0,
                            })
                          }
                          className="h-10 w-20 rounded-lg border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100 outline-none focus:border-indigo-400"
                        />
                        %
                      </label>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-zinc-500">
                Need one account handled differently? Open that account and set
                “Portal inventory prices” to always or never.
              </p>
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="portal-catalog-look">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Catalog look
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                Show cut-out product shots (background removed) on a branded stone
                backdrop in the public catalog. Only photos you've cleaned up appear
                on the backdrop — the rest stay as normal photos.
              </p>
              <div className="mt-2">
                <Row
                  id="catalog_cutout_backdrop"
                  title="Cut-out shots on a brand backdrop"
                  sub="Master switch for the catalog's cut-out look"
                  value={vis.catalog_cutout_backdrop}
                  onChange={(v) => set("catalog_cutout_backdrop", v)}
                />
                <Row
                  id="catalog_cutout_slabs"
                  title="Apply to slabs & remnants"
                  sub="Use the backdrop on the inventory catalog"
                  value={vis.catalog_cutout_slabs}
                  onChange={(v) => set("catalog_cutout_slabs", v)}
                />
                <Row
                  id="catalog_cutout_sinks"
                  title="Apply to sinks"
                  sub="Use the backdrop on the sinks catalog"
                  value={vis.catalog_cutout_sinks}
                  onChange={(v) => set("catalog_cutout_sinks", v)}
                />
              </div>
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Welcome note
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                Shown at the top of the portal home page — a greeting, opening
                hours or who to call.
              </p>
              <textarea
                data-testid="portal-welcome-note"
                rows={3}
                value={vis.welcome_note || ""}
                onChange={(e) => set("welcome_note", e.target.value)}
                placeholder="Thanks for choosing us — call the shop on 314-638-0500 with any questions."
                className="mt-3 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400"
              />
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-5">
              <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-500">
                Material holds
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                How long a hold lasts before the slab or remnant goes back into
                stock on its own. We email the customer a reminder first.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Hold lasts (days)
                  </span>
                  <input
                    data-testid="portal-hold-expiry-days"
                    type="number"
                    min={0}
                    max={365}
                    value={vis.hold_expiry_days ?? 14}
                    onChange={(e) =>
                      set("hold_expiry_days", Number(e.target.value))
                    }
                    className="font-mono-data mt-1 h-11 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none focus:border-indigo-400"
                  />
                  <span className="mt-1 block text-xs text-zinc-600">
                    0 turns expiry off — holds stay until someone clears them.
                  </span>
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Remind the customer (days before)
                  </span>
                  <input
                    data-testid="portal-hold-reminder-days"
                    type="number"
                    min={0}
                    max={60}
                    value={vis.hold_reminder_days ?? 3}
                    onChange={(e) =>
                      set("hold_reminder_days", Number(e.target.value))
                    }
                    className="font-mono-data mt-1 h-11 w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-100 outline-none focus:border-indigo-400"
                  />
                  <span className="mt-1 block text-xs text-zinc-600">
                    0 sends no reminder — the hold just ends.
                  </span>
                </label>
              </div>
              <div className="mt-2">
                <Row
                  id="hold_expiry_portal_only"
                  title="Only holds asked for in the portal"
                  sub="Leave on so holds your crew enters by hand never expire on their own"
                  value={vis.hold_expiry_portal_only}
                  onChange={(v) => set("hold_expiry_portal_only", v)}
                />
              </div>
            </section>

            <div className="flex justify-end">
              <Button
                data-testid="portal-settings-save"
                disabled={busy}
                onClick={save}
                className="h-11 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {busy ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </div>
        )}
        <ColourAutoApplyCard />
      </main>
    </div>
  );
}
